/**
 * Normalized `NEXT_PUBLIC_BASE_PATH` with any trailing slash removed.
 *
 * The Next.js basePath is configured via `next.config.ts` and exposed at build
 * time through `process.env.NEXT_PUBLIC_BASE_PATH`. It may be unset (empty
 * string in dev) or set with or without a trailing slash depending on the
 * environment. Consumers prefixing absolute public asset paths should use this
 * constant (or {@link withBasePath}) so the trimming rule cannot drift between
 * modules.
 *
 * @example
 * ```ts
 * import { normalizedBasePath } from "@/helpers/basePath";
 *
 * const heroImage = `${normalizedBasePath}/images/hero/foo.png`;
 * ```
 */
export const normalizedBasePath: string = (
  process.env.NEXT_PUBLIC_BASE_PATH ?? ""
).replace(/\/$/, "");

/**
 * Prefix a public asset sub-path with the normalized base path. Accepts
 * sub-paths with or without a leading slash; a leading slash is added when
 * missing so the result is always well-formed.
 *
 * Prefer this helper over inline string concatenation when the sub-path is
 * computed at the call site (e.g., from props), and use {@link normalizedBasePath}
 * directly when building a module-level constant prefix.
 *
 * @example
 * ```ts
 * withBasePath("/videos/intro.mp4"); // "<basePath>/videos/intro.mp4"
 * withBasePath("videos/intro.mp4");  // "<basePath>/videos/intro.mp4"
 * ```
 */
export const withBasePath = (subPath: string): string => {
  const leading = subPath.startsWith("/") ? subPath : `/${subPath}`;
  return `${normalizedBasePath}${leading}`;
};
